"use client";

import { Progress as ProgressPrimitive } from "@base-ui/react/progress";
import type React from "react";
import type { Omittable } from "../../lib/props";
import { cn } from "../../lib/utils";
import {
  GAUGE_INDICATOR_CLASS,
  GAUGE_TRACK_CLASS,
  GAUGE_TRACK_THICKNESS,
  type GaugeThickness,
} from "./gauge-recipe";

export function Progress({
  className,
  children,
  ...props
}: ProgressPrimitive.Root.Props): React.ReactElement {
  return (
    <ProgressPrimitive.Root
      className={cn("flex w-full flex-col gap-2", className)}
      data-slot="progress"
      {...props}
    >
      {children ? (
        children
      ) : (
        <ProgressTrack>
          <ProgressIndicator />
        </ProgressTrack>
      )}
    </ProgressPrimitive.Root>
  );
}

export function ProgressLabel({
  className,
  ...props
}: ProgressPrimitive.Label.Props): React.ReactElement {
  return (
    <ProgressPrimitive.Label
      className={cn("font-medium text-sm", className)}
      data-slot="progress-label"
      {...props}
    />
  );
}

export type ProgressTrackProps = ProgressPrimitive.Track.Props &
  Omittable<{
    /** Track thickness on the gauge scale; `md` is a progress bar's rung. */
    size?: GaugeThickness;
  }>;

export function ProgressTrack({
  size = "md",
  className,
  ...props
}: ProgressTrackProps): React.ReactElement {
  return (
    <ProgressPrimitive.Track
      className={cn(GAUGE_TRACK_CLASS, GAUGE_TRACK_THICKNESS[size], className)}
      data-slot="progress-track"
      {...props}
    />
  );
}

export function ProgressIndicator({
  className,
  ...props
}: ProgressPrimitive.Indicator.Props): React.ReactElement {
  return (
    <ProgressPrimitive.Indicator
      className={cn("bg-primary", GAUGE_INDICATOR_CLASS, className)}
      data-slot="progress-indicator"
      {...props}
    />
  );
}

export function ProgressValue({
  className,
  ...props
}: ProgressPrimitive.Value.Props): React.ReactElement {
  return (
    <ProgressPrimitive.Value
      className={cn("text-sm tabular-nums", className)}
      data-slot="progress-value"
      {...props}
    />
  );
}
